import { create } from 'zustand';
import { registerAuxiliaryDrawerCloser } from '../drawer/createDrawerStore';

/**
 * Drawer state for the seller-facing "Create invoice from delivered
 * legs" picker. Unlike `useInvoiceDrawerStore`, this drawer doesn't
 * focus on a single entity id — it scopes by `clientId` (the
 * counterparty CRM Client) and needs the viewer's org id so the
 * resulting invoice is attributed correctly. We therefore use a
 * custom store rather than the generic `createDrawerStore` factory.
 *
 * One instance of `<EligibleLegsDrawer />` should be mounted near
 * the page root (e.g. in `CRMWorkspacePage` / `AccountsPage`); any
 * surface — table header, empty state, banner — can then trigger it
 * via `useEligibleLegsDrawerStore.getState().open({ clientId,
 * viewerOrgId })`.
 */
export type EligibleLegsDrawerState = {
  isOpen: boolean;
  /** CRM client id to scope eligible legs to (buyer side). */
  clientId?: string;
  /** Viewer's org id — pinned as the seller side server-side too. */
  viewerOrgId?: string;
  open: (info: { clientId?: string; viewerOrgId: string }) => void;
  close: () => void;
};

export const useEligibleLegsDrawerStore = create<EligibleLegsDrawerState>(set => ({
  isOpen: false,
  clientId: undefined,
  viewerOrgId: undefined,
  open: info =>
    set({
      isOpen: true,
      clientId: info.clientId,
      viewerOrgId: info.viewerOrgId,
    }),
  close: () => set({ isOpen: false }),
}));

registerAuxiliaryDrawerCloser(() => useEligibleLegsDrawerStore.getState().close());
